import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Container, Grid, Typography } from '@mui/material';
// import { useDispatch } from 'react-redux';
// import { CreateContactRequest } from '../../redux/actions/contactRequestsActions';
import './styles.scss';

// ─────────────────────────────────────────────────────────────────────────────
// Static data
// ─────────────────────────────────────────────────────────────────────────────

const steps = [
  {
    n: '01',
    title: 'Ingest',
    body: 'Draft CBP Form 7501 before filing, a filed 7501, or your own entry pivot table. Header and all line items, including continuation sheets, extracted into structured rows.',
  },
  {
    n: '02',
    title: 'Derive',
    body: 'A deterministic engine computes, from ingested statutory rules, the Chapter 99 code chain, rates, duty basis, and line roles that the entry facts require.',
  },
  {
    n: '03',
    title: 'Compare',
    body: 'Declared against derived, line by line. Every discrepancy is surfaced as a finding rather than silently reconciled.',
  },
  {
    n: '04',
    title: 'Explain',
    body: 'Each finding carries a citation back to the governing statutory text, verbatim as published, for the broker, the reviewer, and the auditor.',
  },
];

const layers = [
  {
    layer: 'Obligation engine',
    fn: 'Determines what is owed. A deterministic query over human-approved statutory rules.',
  },
  {
    layer: 'Explanation engine',
    fn: "Explains the obligation engine's output to importers, brokers, and auditors.",
  },
  {
    layer: 'Change detection',
    fn: 'Surfaces statutory changes and proposes corresponding rule updates.',
  },
];

const commitments = [
  {
    term: 'Human approval',
    detail: 'No machine-extracted statutory rule takes effect until a person reviews and approves it.',
  },
  {
    term: 'Verbatim source text',
    detail: 'Statutory text is stored in the words the issuing authority published. Auditors need the original language, so the original language is what we keep.',
  },
  {
    term: 'No hard deletes',
    detail: 'Rules are closed with effective-date ranges, never erased. The treatment that applied on the date of your entry stays retrievable.',
  },
  {
    term: 'Citation on every finding',
    detail: 'Each discrepancy resolves to the governing statutory text. Nothing is surfaced that cannot be traced.',
  },
];


const reviewStages = [
  {
    label: 'Pre-entry',
    title: 'Before the entry is filed',
    body: 'The derivation runs against the draft entry while it can still be changed. Discrepancies are resolved by editing the entry, not by correcting it later. Nothing reaches CBP wrong, and there is no penalty or interest exposure to argue about.',
    input: 'Requires: draft CBP Form 7501 or entry pivot table, commercial invoice, transport documents',
  },
  {
    label: 'Post-entry',
    title: 'After the entry is filed',
    body: 'The derivation runs against filed entries and surfaces what should have been declared. Each finding carries its citation and the reasoning behind it, so your broker has what they need to prepare a Post Summary Correction rather than reconstructing the analysis.',
    input: 'Requires: filed CBP Form 7501, commercial invoice, transport documents',
  },
];

const reviewFacts = [
  {
    term: 'Errors run both ways',
    detail: 'An independent derivation finds what was underpaid and what was never owed. Underpayment is exposure. Overpayment is your money sitting with CBP. Exemption and special-provision claims are where this concentrates.',
  },
  {
    term: 'Every line, not a sample',
    detail: 'Sampling is a constraint of human review — a person can only read so many entries. The engine derives every line of every entry it ingests, so expert attention goes to the findings rather than to the search.',
  },
  {
    term: 'We do not file',
    detail: 'LUSID Trade produces the finding, the derivation, and the citation. Your customs broker files. We are not a broker, we do not transmit to CBP, and we do not sit between you and the filer.',
  },
  {
    term: 'Scope today',
    detail: 'The engine derives Section 232 metal-content provisions, including melt-and-pour and smelt-and-cast declaration rules, and Chapter 98 special provisions. Design partners set what comes next. Ask us directly about a provision you care about and you will get a straight answer about whether it is built.',
  },
];

// Worked example. Illustrative — built on a real error class, not a live entry.
const findingColumns = [
  {
    title: 'As Declared',
    lines: [
      { text: 'Chapter 99 line — Section 232 steel content' },
      { text: 'Base HTS classification', note: 'Correct, and last on the line as required' },
    ],
  },
  {
    title: 'As Derived',
    lines: [
      { text: 'Chapter 99 line — Section 232 steel content' },
      {
        text: 'Companion Chapter 99 line — smelt-and-cast country declaration',
        note: 'Required by the entry facts. Not declared.',
        added: true,
      },
      { text: 'Base HTS classification', note: 'Agrees' },
    ],
  },
];

const findingDetails = [
  {
    term: 'Finding',
    detail: 'The companion line carrying the country-of-melt-and-pour declaration was omitted. The base classification and the primary Section 232 line are both correct, which is why an internal consistency check would not catch this.',
  },
  {
    term: 'Why it matters',
    detail: 'An omitted metal-content declaration is among the most frequently cited errors on a CBP Notice of Action. It surfaces months after filing, when the only remedy is a correction with penalty and interest attached.',
  },
  {
    term: 'Governing text',
    detail: 'Exact Chapter 99 heading and subheading, verified against the HTSUS revision in force.',
  },
  { term: 'Illustrative exposure', detail: '$2,455', mono: true },
  {
    term: 'If found pre-entry',
    detail: 'Add the companion line before transmission. No correction, no exposure.',
  },
  {
    term: 'If found post-entry',
    detail: 'Route to your broker with the derivation and citation attached for a Post Summary Correction.',
  },
];

const faqs = [
  {
    q: 'Do you file anything with CBP on our behalf?',
    a: [
      'No. LUSID Trade is not a customs broker and does not transmit to CBP. It derives what your entry facts require, compares that against what was declared, and hands you a finding with its citation. Your broker files the entry and any Post Summary Correction, exactly as they do now.',
      'This is deliberate. We are not trying to replace your broker relationship, and a tool that both prepares and audits the same filing has no independent check in it.',
    ],
  },
  {
    q: 'What happens if our broker disagrees with a finding?',
    a: [
      'Every finding carries the statutory text it rests on, verbatim as published, and the entry facts that triggered it. The disagreement becomes a specific argument about a specific provision rather than a dispute between two opinions.',
    ],
  },
  {
    q: 'Do we have to change how our broker files?',
    a: [
      "No. There is no integration, no change to your broker's software, and no change to your filing process. You send the entry data — a draft 7501, a filed 7501, or your own entry pivot table — and receive findings back.",
    ],
  },
  {
    q: 'Does this find money, or only risk?',
    a: [
      'Both, because an independent derivation does not care which direction an error runs. A duty that should have been declared and was not is exposure. A duty that was charged when a Chapter 98 provision or an exemption should have applied is money you are owed.',
    ],
  },
  {
    q: 'Is this just a language model guessing at tariff treatment?',
    a: [
      'No, and the architecture is the reason. Obligation is determined by a deterministic engine running statutory rules that a person has reviewed and approved. The language model explains that determination and proposes rule updates for human review. It never computes what you owe, and it cannot override the engine that does.',
    ],
  },
  {
    q: 'What does the engine cover today?',
    a: [
      'Section 232 metal-content provisions for steel, aluminum, and copper, including the melt-and-pour and smelt-and-cast declaration rules; Chapter 98 special provisions including American goods returned and repairs and alterations. Entry Type 01 formal entries.',
      'Coverage is expanding, and we would rather tell you directly what is built than publish a list that ages. Name the provisions carrying your exposure and you will get a straight answer.',
    ],
  },
];

const audienceFacts = [
  {
    term: 'Primary buyer',
    detail: 'The importer of record — the trade compliance lead, or the CFO or controller at companies without a dedicated compliance function.',
  },
  {
    term: 'Best fit',
    detail: 'High formal-entry volume with complex classification exposure: significant Chapter 98 activity, or heavy Section 232 metal content, or both.',
  },
  {
    term: 'Secondary users',
    detail: 'Customs brokers and outside auditors work from the explanation layer — the citation and reasoning behind each finding, in the form they need to act on it.',
  },
  {
    term: 'Not a fit today',
    detail: 'Low formal-entry volume, entry types other than 01, or exposure concentrated entirely in build-path areas.',
  },
];


// const emptyForm = {
//   company: '',
//   email: '',
//   role: '',
//   entryVolume: '',
//   chapters: '',
//   cf29: '',
//   note: '',
// };

const FactList = ({ facts }) => (
  <Box component="dl" className="trd-facts">
    {facts.map(({ term, detail }) => (
      <Box key={term} className="trd-facts__row">
        <Typography component="dt" className="trd-facts__term">{term}</Typography>
        <Typography component="dd" className="trd-facts__detail">{detail}</Typography>
      </Box>
    ))}
  </Box>
);

const Claim = ({ headline, why, className = '' }) => (
  <Box className={`trd-claim ${className}`.trim()}>
    <Typography className="trd-claim__headline">{headline}</Typography>
    <Typography className="trd-claim__why">{why}</Typography>
  </Box>
);

const TradePage = () => {
  const navigate = useNavigate();
  // const dispatch = useDispatch();

  // const [formData, setFormData] = useState(emptyForm);
  // const [submitted, setSubmitted] = useState(false);
  // const [loading, setLoading] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // const handleInputChange = (e) => {
  //   const { name, value } = e.target;
  //   setFormData((prevState) => ({ ...prevState, [name]: value }));
  // };

  // The contact-requests endpoint takes { name, email, message }; the design
  // partner qualifiers are folded into the message body so they reach the same inbox.
  // const buildMessage = () => [
  //   'LUSID Trade — design partner enquiry',
  //   `Role: ${formData.role || '—'}`,
  //   `Annual formal entry volume: ${formData.entryVolume}`,
  //   `Primary exposure (chapters / trade actions): ${formData.chapters}`,
  //   `CF-29 in the last 24 months: ${formData.cf29 || '—'}`,
  //   `Notes: ${formData.note || '—'}`,
  // ].join('\n');

  // const handleSubmit = async (e) => {
  //   e.preventDefault();
  //   setLoading(true);

  //   try {
  //     await dispatch(
  //       CreateContactRequest(
  //         { name: formData.company, email: formData.email, message: buildMessage() },
  //         setLoading
  //       )
  //     );
  //     setFormData(emptyForm);
  //     setSubmitted(true);
  //     setTimeout(() => setSubmitted(false), 6000);
  //   } catch (error) {
  //     console.error('Trade design partner submission failed:', error);
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  return (
    <Box className="trd-page">

      {/* ── Status strip ─────────────────────────────────────── */}
      <Box className="trd-status">
        <Container maxWidth="lg">
          <Typography className="trd-status__text">
            <strong>Status:</strong> in development. Working with a design partner ahead of general
            availability. Not yet generally released.
          </Typography>
        </Container>
      </Box>

      {/* ── Hero ─────────────────────────────────────────────── */}
      <Box className="trd-hero">
        <Box className="trd-hero__grid-bg" />
        <Container maxWidth="lg">
          <Box className="trd-hero__content">
            <Typography className="trd-hero__overline">LUSID Trade</Typography>
            <Typography variant="h1" className="trd-hero__title">
              Know what your entry should say before it is filed.
            </Typography>
            <Typography className="trd-hero__subtitle">
              LUSID Trade independently derives the Chapter 99 code chain, rates, duty basis, and
              entry line roles that your entry facts require under the HTSUS — then compares that
              derivation, line by line. Discrepancies surface while you can still act on them.
            </Typography>
            <Box className="trd-hero__cta-row">
              <Button
                variant="contained"
                className="trd-hero__cta"
                onClick={() => navigate('/contact-us')}
              >
                Become a design partner
              </Button>
              <Typography className="trd-hero__note">Entry Type 01, US formal entries.</Typography>
            </Box>

            <Claim
              className="trd-claim--invert"
              headline="The AI never decides what you owe."
              why="Obligation is determined by a deterministic engine running human-approved statutory rules. The language model explains that determination and helps propose rule updates for human review. It does not compute your duty, and it cannot override the engine that does."
            />
          </Box>
        </Container>
      </Box>

      {/* ── The problem ──────────────────────────────────────── */}
      <Box className="trd-section">
        <Container maxWidth="lg">
          <Box className="trd-section__head">
            <Typography className="trd-section__overline">The problem</Typography>
            <Typography variant="h2" className="trd-section__title">
              By the time CBP tells you the entry was wrong, your only remedy is a correction
            </Typography>
            <Typography className="trd-section__lede">
              Chapter 99 secondary classifications — Section 232, Section 301, IEEPA, 
              Section 201, MTB, quota provisions — stack on top of the base classification, 
              and they change faster than anything else on an entry. No single party sees 
              the whole picture: the facts, the filing, and the governing text sit in three 
              different places, and nothing checks them against each other before transmission. 
              Errors surface months later as a CF-29, when the entry is filed and the only remedy 
              is a Post Summary Correction, with penalty and interest exposure attached.
            </Typography>
            <Typography className="trd-section__lede">
              The rule set does not hold still. The USITC republishes the HTSUS as Basic 
              Editions with numbered Revisions, and tariff actions arrive by proclamation 
              and notice on their own schedule. What governs your entry is the text in force 
              on the date of entry — not the text in force when someone looks it up.
            </Typography>
            <Typography className="trd-section__lede">
              Section 232 metal content is where the engine starts. Steel, aluminum and copper 
              provisions require melt-and-pour and smelt-and-cast declarations that are easy to 
              omit, which makes them the highest-value place to begin. The method is not specific 
              to Section 232: deriving the obligation independently and comparing it line by line 
              applies to every trade action that stacks in Chapter 99, and the engine extends one 
              provision family at a time.
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* ── How it works ─────────────────────────────────────── */}
      <Box className="trd-section trd-section--tinted" id="trd-how">
        <Container maxWidth="lg">
          <Box className="trd-section__head">
            <Typography className="trd-section__overline">How it works</Typography>
            <Typography variant="h2" className="trd-section__title">
              An independent second derivation, not a spell-check
            </Typography>
            <Typography className="trd-section__lede">
              LUSID Trade does not check the entry against itself. It derives the
              answer independently from the entry facts and the governing statutory text, then puts
              the two side by side.
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {steps.map(({ n, title, body }) => (
              <Grid item xs={12} sm={6} md={3} key={n}>
                <Box className="trd-step">
                  <Typography className="trd-step__number">{n}</Typography>
                  <Typography className="trd-step__title">{title}</Typography>
                  <Typography className="trd-step__desc">{body}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>

          <Claim
            className="trd-claim--spaced"
            headline="The extractor transcribes what the document says — including the errors."
            why="It never corrects a code to what it thinks is right. Discrepancies are the output of this product, not noise to be cleaned up before you see them."
          />
        </Container>
      </Box>

      {/* ── Why you can trust the output ─────────────────────── */}
      <Box className="trd-section" id="trd-trust">
        <Container maxWidth="lg">
          <Box className="trd-section__head">
            <Typography className="trd-section__overline">Why you can trust the output</Typography>
            <Typography variant="h2" className="trd-section__title">
              Three layers, and only one of them decides anything
            </Typography>
            <Typography className="trd-section__lede">
              The reasonable first question about any AI product in this category is whether it is a
              language model guessing at tariff treatment. It is not, and the architecture is the
              reason.
            </Typography>
          </Box>

          <Box className="trd-table-wrap">
            <Box component="table" className="trd-table">
              <Box component="thead">
                <Box component="tr">
                  <Box component="th" className="trd-table__col-layer">Layer</Box>
                  <Box component="th">Function</Box>
                </Box>
              </Box>
              <Box component="tbody">
                {layers.map(({ layer, fn }) => (
                  <Box component="tr" key={layer}>
                    <Box component="td" className="trd-table__layer" data-label="Layer">{layer}</Box>
                    <Box component="td" data-label="Function">{fn}</Box>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>

          <Typography variant="h2" className="trd-section__title trd-section__title--sub">
            Four commitments that hold regardless of what the model says
          </Typography>
          <FactList facts={commitments} />
        </Container>
      </Box>

      {/* ── Review points ────────────────────────────────────── */}
      <Box className="trd-section trd-section--tinted" id="trd-review">
        <Container maxWidth="lg">
          <Box className="trd-section__head">
            <Typography className="trd-section__overline">Review points</Typography>
            <Typography variant="h2" className="trd-section__title">
              Two points of review, one derivation
            </Typography>
            <Typography className="trd-section__lede">
              The same engine runs before your broker transmits and after. Before filing, a finding
              is an edit. After filing, it is a Post Summary Correction — still worth making, and
              often worth money.
            </Typography>
          </Box>

          <Grid container spacing={3} className="trd-stages">
            {reviewStages.map(({ label, title, body, input }) => (
              <Grid item xs={12} md={6} key={label}>
                <Box className="trd-stage">
                  <Typography className="trd-stage__label">{label}</Typography>
                  <Box className="trd-stage__body">
                    <Typography className="trd-stage__title">{title}</Typography>
                    <Typography className="trd-stage__text">{body}</Typography>
                    <Typography className="trd-stage__input">{input}</Typography>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>

          <FactList facts={reviewFacts} />
        </Container>
      </Box>

      {/* ── Example ──────────────────────────────────────────── */}
      <Box className="trd-section" id="trd-example">
        <Container maxWidth="lg">
          <Box className="trd-section__head">
            <Typography className="trd-section__overline">Example</Typography>
            <Typography variant="h2" className="trd-section__title">
              What a finding looks like
            </Typography>
            <Typography className="trd-section__lede">
              One discrepancy, end to end. This is an illustrative example built on a real error
              class — the figures are not taken from a live entry.
            </Typography>
          </Box>

          <Box className="trd-finding">
            <Box className="trd-finding__head">
              <Typography className="trd-finding__id">
                Line 14 · Steel fasteners · Entered value $24,550
              </Typography>
              <Typography className="trd-finding__badge">Discrepancy</Typography>
            </Box>

            <Box className="trd-finding__cols">
              {findingColumns.map(({ title, lines }) => (
                <Box className="trd-finding__col" key={title}>
                  <Typography className="trd-finding__col-title">{title}</Typography>
                  <Box component="ul" className="trd-lines">
                    {lines.map(({ text, note, added }) => (
                      <Box
                        component="li"
                        key={text}
                        className={`trd-lines__item${added ? ' trd-lines__item--add' : ''}`}
                      >
                        {text}
                        {note && <span className="trd-lines__note">{note}</span>}
                      </Box>
                    ))}
                  </Box>
                </Box>
              ))}
            </Box>

            <Box className="trd-finding__foot">
              <Box component="dl" className="trd-finding__details">
                {findingDetails.map(({ term, detail, mono }) => (
                  <Box className="trd-finding__row" key={term}>
                    <Typography component="dt" className="trd-finding__term">{term}</Typography>
                    <Typography
                      component="dd"
                      className={`trd-finding__detail${mono ? ' trd-finding__detail--mono' : ''}`}
                    >
                      {detail}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>

          <Typography className="trd-section__footnote">
            Rates are deliberately not shown in this example. The applicable rate depends on which
            HTSUS revision was in force on the date of entry, and quoting one out of context is
            exactly the error this product exists to prevent. The engine resolves the rate against
            the rule set effective on that date.
          </Typography>
        </Container>
      </Box>

      {/* ── Who it is for ────────────────────────────────────── */}
      <Box className="trd-section trd-section--tinted">
        <Container maxWidth="lg">
          <Box className="trd-section__head">
            <Typography className="trd-section__overline">Who it is for</Typography>
            <Typography variant="h2" className="trd-section__title">
              Importers of record with real classification exposure
            </Typography>
          </Box>
          <FactList facts={audienceFacts} />
        </Container>
      </Box>

      {/* ── Questions ────────────────────────────────────────── */}
      <Box className="trd-section" id="trd-faq">
        <Container maxWidth="lg">
          <Box className="trd-section__head">
            <Typography className="trd-section__overline">Questions</Typography>
            <Typography variant="h2" className="trd-section__title">
              What importers ask first
            </Typography>
          </Box>

          <Box className="trd-faq">
            {faqs.map(({ q, a }, i) => (
              <Box component="details" className="trd-faq__item" key={q} open={i === 0}>
                <Box component="summary" className="trd-faq__summary">
                  <span className="trd-faq__num">{String(i + 1).padStart(2, '0')}</span>
                  <span className="trd-faq__q">{q}</span>
                  <span className="trd-faq__chevron" aria-hidden="true">
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </span>
                </Box>
                <Box className="trd-faq__answer">
                  {a.map((para) => (
                    <Typography className="trd-faq__text" key={para}>{para}</Typography>
                  ))}
                </Box>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      {/* <Box className="trd-partner" id="trd-partner">
        <Container maxWidth="lg">
          <Box className="trd-partner__head">
            <Typography className="trd-partner__overline">Design partners</Typography>
            <Typography variant="h2" className="trd-partner__title">
              We are taking a small number of design partners before general availability
            </Typography>
            <Typography className="trd-partner__lede">
              A design partner runs real entries through the engine, tells us where the derivation
              disagrees with reality, and helps set the order of the build path. In a product where
              accuracy is the entire proposition, that feedback is worth more than early revenue.
            </Typography>
          </Box>

          {submitted && (
            <Alert severity="success" className="trd-partner__alert">
              Thank you — your enquiry is in. We will be in touch at the email you provided.
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} className="trd-form">
            <Box className="trd-form__field">
              <label htmlFor="t-co">Company<span className="trd-form__req">required</span></label>
              <input id="t-co" name="company" value={formData.company} onChange={handleInputChange} required />
            </Box>
            <Box className="trd-form__field">
              <label htmlFor="t-em">Work email<span className="trd-form__req">required</span></label>
              <input id="t-em" name="email" type="email" value={formData.email} onChange={handleInputChange} required />
            </Box>
            <Box className="trd-form__field">
              <label htmlFor="t-role">Your role</label>
              <input
                id="t-role"
                name="role"
                placeholder="e.g. Trade compliance manager"
                value={formData.role}
                onChange={handleInputChange}
              />
            </Box>
            <Box className="trd-form__field">
              <label htmlFor="t-vol">Annual formal entry volume<span className="trd-form__req">required</span></label>
              <select id="t-vol" name="entryVolume" value={formData.entryVolume} onChange={handleInputChange} required>
                <option value="">Select</option>
                {entryVolumes.map((v) => <option key={v} value={v}>{v}</option>)}
              </select>
            </Box>
            <Box className="trd-form__field trd-form__field--full">
              <label htmlFor="t-ch">
                Which HTSUS chapters or trade actions carry most of your exposure?
                <span className="trd-form__req">required</span>
              </label>
              <input
                id="t-ch"
                name="chapters"
                placeholder="e.g. Chapter 98 repairs, Section 232 steel content, Section 301"
                value={formData.chapters}
                onChange={handleInputChange}
                required
              />
            </Box>
            <Box className="trd-form__field trd-form__field--full">
              <label htmlFor="t-cf">Have you received a CF-29 in the last 24 months?</label>
              <select id="t-cf" name="cf29" value={formData.cf29} onChange={handleInputChange}>
                <option value="">Select</option>
                {cf29Options.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            </Box>
            <Box className="trd-form__field trd-form__field--full">
              <label htmlFor="t-note">Anything else we should know</label>
              <textarea id="t-note" name="note" rows={3} value={formData.note} onChange={handleInputChange} />
            </Box>
            <Box className="trd-form__field trd-form__field--full">
              <Button type="submit" variant="contained" disabled={loading} className="trd-form__submit">
                {loading ? <CircularProgress size={18} thickness={5} sx={{ color: '#fff' }} /> : 'Become a design partner'}
              </Button>
            </Box>
          </Box>
        </Container>
      </Box> */}


      {/* ── Disclosure ───────────────────────────────────────── */}
      <Box className="trd-disclosure">
        <Container maxWidth="lg">
          <Typography className="trd-disclosure__text">
            LUSID Trade produces classification and duty analysis for internal review. It is not
            legal advice, it is not a customs ruling, and it does not substitute for the filing
            responsibility of a licensed customs broker or for the importer of record's own
            obligation to exercise reasonable care under 19 U.S.C. § 1484.
          </Typography>
        </Container>
      </Box>

    </Box>
  );
};

export default TradePage;
